import { parseISO } from "date-fns";

import type { I18nT } from "@lib/dashboardI18n";

import type { ContentOptimizationStepState } from "@canvas/Steps/Steps.types";

import type { ContentOptimizationPhase } from "./types";

export interface ContentOptimizationPhaseBannerCopy {
  bannerTitle: string;
  bannerDescription: string;
  descriptionLinkLabel?: string;
}

/**
 * Single-send steps may still have a persisted backend optimization phase, but that
 * phase describes ongoing optimization and should not be presented in the UI.
 */
export const getContentOptimizationDisplayPhase = (
  phase: ContentOptimizationPhase | null | undefined,
  isSingleSend: boolean
): ContentOptimizationPhase | null => {
  if (isSingleSend) {
    return null;
  }

  return phase ?? null;
};

const secondsPerHour = 60 * 60;
const secondsPerDay = 24 * secondsPerHour;

export const getContentOptimizationPhaseStepState = (
  phase: ContentOptimizationPhase
): ContentOptimizationStepState => {
  switch (phase) {
    case "learning":
      return "learningInProgress";
    case "action_recommended":
      return "actionRecommended";
    case "optimizing":
      return "optimizing";
  }
};

export const getContentOptimizationPhaseLabel = (
  phase: ContentOptimizationPhase,
  t: I18nT<"content-optimization">
): string => {
  switch (phase) {
    case "learning":
      return t("phase-label-learning", "Learning in progress");
    case "action_recommended":
      return t("phase-label-action-recommended", "Action recommended");
    case "optimizing":
      return t("phase-label-optimizing", "Optimizing");
  }
};

/**
 * @param startTimeIso ISO8601 timestamp for the current optimization window start
 * @param nowTimestamp current time in seconds from epoch
 * @return null when start time is missing, invalid, or elapsed time is under one hour;
 *   otherwise a localized "N hours" label before 24 hours elapse, or an "N days" label after
 */
export const getLearningPhaseRunningDurationLabel = (
  startTimeIso: string | null | undefined,
  nowTimestamp: number,
  t: I18nT<"content-optimization">
): string | null => {
  if (!startTimeIso) {
    return null;
  }

  const startDate = parseISO(startTimeIso);
  if (Number.isNaN(startDate.getTime())) {
    return null;
  }

  const elapsedSeconds = nowTimestamp - Math.floor(startDate.getTime() / 1000);
  if (elapsedSeconds < secondsPerHour) {
    return null;
  }

  if (elapsedSeconds < secondsPerDay) {
    return t("phase-analytics-running-hours", "{count, plural, one {1 hour} other {# hours}}", {
      count: Math.floor(elapsedSeconds / secondsPerHour),
    });
  }

  return t("phase-analytics-running-days", "{count, plural, one {1 day} other {# days}}", {
    count: Math.floor(elapsedSeconds / secondsPerDay),
  });
};

export const getContentOptimizationActionRecommendedBanner = (
  t: I18nT<"content-optimization">
): ContentOptimizationPhaseBannerCopy => {
  return {
    bannerTitle: t("phase-banner-title-action-recommended", "Content Optimizer can't find top performers yet"),
    bannerDescription: t(
      "phase-banner-description-action-recommended",
      "This step has been learning longer than usual. Audience size, engagement, variant diversity, and combination count can all affect how quickly it finds top performers."
    ),
    descriptionLinkLabel: t("view-troubleshooting-steps", "View troubleshooting steps"),
  };
};

export const getContentOptimizationPhaseEditBanner = (
  phase: ContentOptimizationPhase,
  t: I18nT<"content-optimization">
): ContentOptimizationPhaseBannerCopy | null => {
  switch (phase) {
    case "learning":
      return {
        bannerTitle: t("phase-banner-title-learning", "Learning in progress"),
        bannerDescription: t(
          "phase-banner-description-learning",
          "Avoid adding or deactivating variants until performance rankings stabilize. Changes reset all learning data and restart learning from scratch."
        ),
      };
    case "action_recommended":
    case "optimizing":
      return null;
  }
};

export const getContentOptimizationPhaseCanvasTooltip = (
  phase: ContentOptimizationPhase,
  tCommonStep: I18nT<"common-step">,
  isEditing: boolean
): string => {
  switch (phase) {
    case "learning":
      return tCommonStep(
        "step-is-learning-in-progress-tooltip",
        "Content Optimizer is learning which combinations work best."
      );
    case "action_recommended":
      if (isEditing) {
        return tCommonStep(
          "step-is-action-recommended-tooltip",
          "Content Optimizer can't find top performers without more data. View step settings for more details."
        );
      }
      return tCommonStep(
        "step-is-action-recommended-analytics-tooltip",
        "Content Optimizer can't find top performers without more data. View step analytics for more details."
      );
    case "optimizing":
      return tCommonStep(
        "step-is-optimizing-tooltip",
        "Content Optimizer is routing traffic to your top-performing combinations."
      );
  }
};
